import { useState, useEffect } from "react";
import { useAuth, useUser } from "@clerk/react";
import { useNavigate, Link } from "@tanstack/react-router";
import type {
  RoomSnapshot,
  RoomConfig,
  SourceSnapshot,
  SourceConfig,
} from "@tuit/shared-types";
import styles from "../styles/components/AdminPage.module.css";
import { TopBar } from "./TopBar";
import { AdminHeader, type PingStatus } from "./admin/AdminHeader";
import { OpsPanel, type InitResult } from "./admin/OpsPanel";
import { RoomConfigTable } from "./admin/RoomConfigTable";
import { RoomTable } from "./admin/RoomTable";
import { SourceConfigTable } from "./admin/SourceConfigTable";
import { SourceTable } from "./admin/SourceTable";

export function AdminPage() {
  const [roomId, setRoomId] = useState("tech");
  const [sourceIdInput, setSourceIdInput] = useState("");
  const [roomConfig, setRoomConfig] = useState<RoomConfig | null>(null);
  const [roomSnapshot, setRoomSnapshot] = useState<RoomSnapshot | null>(null);
  const [sourceConfig, setSourceConfig] = useState<SourceConfig | null>(null);
  const [sourceSnapshot, setSourceSnapshot] = useState<SourceSnapshot | null>(
    null,
  );
  const [loadingRoomConfig, setLoadingRoomConfig] = useState(false);
  const [loadingRoom, setLoadingRoom] = useState(false);
  const [loadingSourceConfig, setLoadingSourceConfig] = useState(false);
  const [loadingSource, setLoadingSource] = useState(false);
  const [clearingSource, setClearingSource] = useState(false);
  const [initRoomsResult, setInitRoomsResult] = useState<InitResult>(null);
  const [initSourcesResult, setInitSourcesResult] = useState<InitResult>(null);
  const [initializingRooms, setInitializingRooms] = useState(false);
  const [initializingSources, setInitializingSources] = useState(false);
  const [pinging, setPinging] = useState(false);
  const [pingStatus, setPingStatus] = useState<PingStatus>(null);
  const [feedbackCount, setFeedbackCount] = useState<number | null>(null);
  const [issueCount, setIssueCount] = useState<number | null>(null);
  const [pendingCount, setPendingCount] = useState<number | null>(null);

  const { isLoaded, userId } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();
  const role = (user?.publicMetadata as { role?: string } | undefined)?.role;
  const isAdmin = role === "admin" || role === "editor";

  useEffect(() => {
    if (isLoaded && (!userId || !isAdmin)) {
      navigate({ to: "/" });
    }
  }, [isLoaded, userId, isAdmin, navigate]);

  useEffect(() => {
    if (!isLoaded || !userId) return;
    fetch("/api/feedback")
      .then((r) => r.json())
      .then((d) => setFeedbackCount((d.entries as unknown[]).length))
      .catch(() => setFeedbackCount(0));
    fetch("/api/issues")
      .then((r) => r.json())
      .then((d) => setIssueCount((d.issues as unknown[]).length))
      .catch(() => setIssueCount(0));
    fetch("/api/admin/catalog/sources?status=pending&pageSize=1")
      .then((r) => r.json())
      .then((d) => setPendingCount((d as { total: number }).total))
      .catch(() => setPendingCount(0));
  }, [isLoaded, userId]);

  if (!isLoaded || !userId || !isAdmin) return null;

  async function initRooms() {
    setInitializingRooms(true);
    setInitRoomsResult(null);
    try {
      const data = await fetch("/api/rooms/initialize", {
        method: "POST",
      }).then((r) => r.json());
      const created = data.rooms.filter(
        (r: { status: string }) => r.status === "created",
      ).length;
      const updated = data.rooms.filter(
        (r: { status: string }) => r.status === "updated",
      ).length;
      const existing = data.rooms.filter(
        (r: { status: string }) => r.status === "exists",
      ).length;
      setInitRoomsResult({ created, updated, existing });
    } finally {
      setInitializingRooms(false);
    }
  }

  async function initSources() {
    setInitializingSources(true);
    setInitSourcesResult(null);
    try {
      const data = await fetch("/api/sources/initialize", {
        method: "POST",
      }).then((r) => r.json());
      const created = data.sources.filter(
        (s: { status: string }) => s.status === "created",
      ).length;
      const updated = data.sources.filter(
        (s: { status: string }) => s.status === "updated",
      ).length;
      const existing = data.sources.filter(
        (s: { status: string }) => s.status === "exists",
      ).length;
      setInitSourcesResult({ created, updated, existing });
    } finally {
      setInitializingSources(false);
    }
  }

  async function selectRoom(id: string) {
    setRoomId(id);
    setLoadingRoomConfig(true);
    setLoadingRoom(true);
    setRoomConfig(null);
    setRoomSnapshot(null);
    try {
      const [configRes, snapshotRes] = await Promise.all([
        fetch(`/api/room/${id}/config`),
        fetch(`/api/room/${id}/snapshot`),
      ]);
      setRoomConfig(await configRes.json());
      setRoomSnapshot(await snapshotRes.json());
    } finally {
      setLoadingRoomConfig(false);
      setLoadingRoom(false);
    }
  }

  async function loadRoomConfig() {
    setLoadingRoomConfig(true);
    setRoomConfig(null);
    try {
      const res = await fetch(`/api/room/${roomId}/config`);
      setRoomConfig(await res.json());
    } finally {
      setLoadingRoomConfig(false);
    }
  }

  async function loadRoomSnapshot() {
    setLoadingRoom(true);
    setRoomSnapshot(null);
    try {
      const res = await fetch(`/api/room/${roomId}/snapshot`);
      setRoomSnapshot(await res.json());
    } finally {
      setLoadingRoom(false);
    }
  }

  async function loadSourceConfig() {
    const id = sourceIdInput.trim();
    if (!id) return;
    setLoadingSourceConfig(true);
    setSourceConfig(null);
    try {
      const res = await fetch(`/api/source/${id}/config`);
      setSourceConfig(await res.json());
    } finally {
      setLoadingSourceConfig(false);
    }
  }

  async function loadSourceSnapshot() {
    const id = sourceIdInput.trim();
    if (!id) return;
    setLoadingSource(true);
    setSourceSnapshot(null);
    try {
      const res = await fetch(`/api/source/${id}/snapshot`);
      setSourceSnapshot(await res.json());
    } finally {
      setLoadingSource(false);
    }
  }

  async function selectSource(id: string) {
    setSourceIdInput(id);
    setLoadingSourceConfig(true);
    setLoadingSource(true);
    setSourceConfig(null);
    setSourceSnapshot(null);
    try {
      const [configRes, snapshotRes] = await Promise.all([
        fetch(`/api/source/${id}/config`),
        fetch(`/api/source/${id}/snapshot`),
      ]);
      setSourceConfig(await configRes.json());
      setSourceSnapshot(await snapshotRes.json());
    } finally {
      setLoadingSourceConfig(false);
      setLoadingSource(false);
    }
  }

  async function clearSourceSnapshot() {
    const id = sourceIdInput.trim();
    if (!id) return;
    setClearingSource(true);
    setSourceSnapshot(null);
    try {
      await fetch(`/api/source/${id}/snapshot`, { method: "DELETE" });
    } finally {
      setClearingSource(false);
    }
  }

  async function ping() {
    setPinging(true);
    setPingStatus(null);
    const start = Date.now();
    try {
      const res = await fetch("/api/ping");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setPingStatus({ ok: true, latencyMs: Date.now() - start });
    } catch (err) {
      setPingStatus({ ok: false, error: String(err) });
    } finally {
      setPinging(false);
    }
  }

  return (
    <div className={styles.page}>
      <TopBar />
      <div className={styles.content}>
      <AdminHeader pinging={pinging} pingStatus={pingStatus} onPing={ping} />
      <div className={styles.statCards}>
        <Link to="/admin/user_feedback" className={styles.statCard}>
          <span className={styles.statLabel}>User Feedback</span>
          <span className={`${styles.statCount} ${feedbackCount ? styles.statCountAlert : ""}`}>
            {feedbackCount === null ? "…" : feedbackCount}
          </span>
          <span className={styles.statSub}>outstanding →</span>
        </Link>
        <Link to="/admin/reported_issues" className={styles.statCard}>
          <span className={styles.statLabel}>Reported Issues</span>
          <span className={`${styles.statCount} ${issueCount ? styles.statCountAlert : ""}`}>
            {issueCount === null ? "…" : issueCount}
          </span>
          <span className={styles.statSub}>outstanding →</span>
        </Link>
        <Link to="/admin/feeds" className={styles.statCard}>
          <span className={styles.statLabel}>Feeds Editor</span>
          <span className={`${styles.statCount} ${styles.statCountNav}`}>→</span>
          <span className={styles.statSub}>manage sources</span>
        </Link>
        <Link to="/admin/rooms" className={styles.statCard}>
          <span className={styles.statLabel}>Rooms Editor</span>
          <span className={`${styles.statCount} ${styles.statCountNav}`}>→</span>
          <span className={styles.statSub}>manage system rooms</span>
        </Link>
        <Link to="/admin/catalog/import" className={styles.statCard}>
          <span className={styles.statLabel}>Import OPML</span>
          <span className={`${styles.statCount} ${styles.statCountNav}`}>→</span>
          <span className={styles.statSub}>add sources to catalog</span>
        </Link>
        <Link to="/admin/catalog/review" className={styles.statCard}>
          <span className={styles.statLabel}>Review Queue</span>
          <span className={`${styles.statCount} ${pendingCount ? styles.statCountAlert : ""}`}>
            {pendingCount === null ? "…" : pendingCount}
          </span>
          <span className={styles.statSub}>pending →</span>
        </Link>
        <Link to="/admin/sitemap" className={styles.statCard}>
          <span className={styles.statLabel}>Site Map</span>
          <span className={`${styles.statCount} ${styles.statCountNav}`}>→</span>
          <span className={styles.statSub}>all routes</span>
        </Link>
      </div>
      <div className={styles.devSection}>
        <span className={styles.devSectionLabel}>In Development</span>
        <div className={styles.statCards}>
          <Link to="/settings/digest" className={styles.statCard}>
            <span className={styles.statLabel}>Email Digest</span>
            <span className={`${styles.statCount} ${styles.statCountNav}`}>→</span>
            <span className={styles.statSub}>digest settings</span>
          </Link>
          <Link to="/settings/filters" className={styles.statCard}>
            <span className={styles.statLabel}>Keyword Filters</span>
            <span className={`${styles.statCount} ${styles.statCountNav}`}>→</span>
            <span className={styles.statSub}>content filters</span>
          </Link>
          <Link to="/settings/webhooks" className={styles.statCard}>
            <span className={styles.statLabel}>Webhooks</span>
            <span className={`${styles.statCount} ${styles.statCountNav}`}>→</span>
            <span className={styles.statSub}>outbound hooks</span>
          </Link>
          <Link to="/settings/rules" className={styles.statCard}>
            <span className={styles.statLabel}>Rules</span>
            <span className={`${styles.statCount} ${styles.statCountNav}`}>→</span>
            <span className={styles.statSub}>automation rules</span>
          </Link>
          <Link to="/settings/integrations" className={styles.statCard}>
            <span className={styles.statLabel}>Integrations</span>
            <span className={`${styles.statCount} ${styles.statCountNav}`}>→</span>
            <span className={styles.statSub}>Slack / Discord / Teams</span>
          </Link>
          <Link to="/team" className={styles.statCard}>
            <span className={styles.statLabel}>Team</span>
            <span className={`${styles.statCount} ${styles.statCountNav}`}>→</span>
            <span className={styles.statSub}>team management</span>
          </Link>
        </div>
      </div>
      <OpsPanel
        roomId={roomId}
        onRoomIdChange={selectRoom}
        sourceIdInput={sourceIdInput}
        onSourceIdInputChange={setSourceIdInput}
        roomSnapshot={roomSnapshot}
        sourceSnapshot={sourceSnapshot}
        loadingRoomConfig={loadingRoomConfig}
        loadingRoom={loadingRoom}
        loadingSourceConfig={loadingSourceConfig}
        loadingSource={loadingSource}
        clearingSource={clearingSource}
        initializingRooms={initializingRooms}
        initializingSources={initializingSources}
        initRoomsResult={initRoomsResult}
        initSourcesResult={initSourcesResult}
        onInitRooms={initRooms}
        onInitSources={initSources}
        onLoadRoomConfig={loadRoomConfig}
        onLoadRoom={loadRoomSnapshot}
        onLoadSourceConfig={loadSourceConfig}
        onLoadSource={loadSourceSnapshot}
        onClearSource={clearSourceSnapshot}
      />
      <div className={styles.sourceRow}>
        <div className={styles.panelGroup}>
          <RoomConfigTable config={roomConfig} roomId={roomId} onSelectSource={selectSource} />
          <RoomTable
            snapshot={roomSnapshot}
            roomId={roomId}
            onSelectSource={selectSource}
          />
        </div>
        <div className={styles.panelGroup}>
          <SourceConfigTable config={sourceConfig} sourceId={sourceIdInput} />
          <SourceTable snapshot={sourceSnapshot} sourceId={sourceIdInput} />
        </div>
      </div>
      </div>
    </div>
  );
}
